import { useEffect, useState } from 'react';
import { getHqAccessMe, HqAccessMe } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { fallbackKeysFromGroups } from './hqPermissionsModel';

/**
 * BUILD-039 — the signed-in person's CURRENT Unique HQ permissions, as the SERVER resolves them (role + individual permissions), for what
 * the navigation and screens SHOW. It is never the permission itself: every request is authorized again by the server. One answer is shared
 * per page load; `refreshHqAccess()` forgets it (after an administrator changes their own view of access, or on focus after a while).
 * If the server cannot answer (an older server without the route), the screens fall back to today's sign-in-group behaviour.
 */
export interface HqAccessView {
  loading: boolean;
  keys: Set<string>;
  can: (key: string) => boolean;
  canManageAccess: boolean;
  companyAdmin: boolean;
  platformOwner: boolean;
  primaryOwner: boolean;
  source: 'platform' | 'legacy-groups' | 'fallback';
}

let cached: { at: number; promise: Promise<HqAccessMe | null> } | null = null;
const MAX_AGE_MS = 30000;
const listeners = new Set<() => void>();
export function refreshHqAccess() { cached = null; for (const l of listeners) l(); }
function ask(): Promise<HqAccessMe | null> {
  if (!cached || Date.now() - cached.at > MAX_AGE_MS) {
    cached = { at: Date.now(), promise: getHqAccessMe().then((r) => (r.ok && r.body && Array.isArray((r.body as any).keys) ? (r.body as HqAccessMe) : null)).catch(() => null) };
  }
  return cached.promise;
}

function viewOf(me: HqAccessMe | null, groups: string[], loading: boolean): HqAccessView {
  const keys = me ? new Set(me.keys) : fallbackKeysFromGroups(groups);
  const isOwnerGroup = groups.includes('owner'); const isAdminGroup = groups.includes('admin');
  return {
    loading, keys, can: (k) => keys.has(k),
    canManageAccess: me ? me.canManageAccess : (isOwnerGroup || isAdminGroup),
    companyAdmin: me ? me.companyAdmin : false,
    platformOwner: me ? me.platformOwner.isOwner : false,
    primaryOwner: me ? me.platformOwner.isPrimary : false,
    source: me ? me.source : 'fallback',
  };
}

export function useHqAccess(): HqAccessView {
  const { user } = useAuth();
  const groups = user?.groups || [];
  const [me, setMe] = useState<HqAccessMe | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    const load = () => ask().then((a) => { if (live) { setMe(a); setLoading(false); } });
    load();
    listeners.add(load);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { live = false; listeners.delete(load); window.removeEventListener('focus', onFocus); };
  }, []);
  return viewOf(me, groups, loading);
}
